import axios from 'axios';
import { Dispatch } from 'redux';

import { logout } from '../services/redux/authSlice';
import { BusinessProceedingsResponse } from './types';
import { Evaluation } from './types/models';

export const api = axios.create({
  baseURL: `${process.env.REACT_APP_BACKEND_URL}/api`, //'https://api.faaast.cl',
  withCredentials: true,
});

export const versionApi = axios.create({
  baseURL: `${process.env.PUBLIC_URL}/version.json`,
});

export const requestHandlerPublic = async ({
  method,
  url,
  data,
  headers,
  responseType,
}: RequestHandlerParams) => {
  try {
    const options: any = {
      method,
      url,
      data,
      headers,
    };
    if (responseType) options.responseType = responseType;

    return await api.request(options);
  } catch (error: any) {
    if (error.response && error.response.status === 500) {
      console.error(error);
      throw error;
      // } else if (error.response && error.response.status === 404) {
      //   window.location.href = '/'
    } else {
      console.error(error);
      throw error;
    }
  }
};

export const authenticateUser = async (
  username: string,
  password: string,
  secretId?: string,
) => {
  try {
    const response = await api.post('public/auth/login', {
      email: username,
      password,
      secretId,
    });
    return response.data;
  } catch (error: any) {
    console.error(error);
    if (error.response && error.response.status === 401) {
      return false;
    } else {
      console.error(error);
      throw error;
    }
  }
};

export const sendPasswordResetEmail = async (data: any) => {
  const response = await requestHandlerPublic({
    method: 'post',
    url: 'public/auth/sendPasswordResetEmail',
    data,
  });
  if (response) return response.data;
};

export const confirmUserRegistrationCode = async (data: any) => {
  const response = await requestHandlerPublic({
    method: 'post',
    url: 'public/auth/confirmUserRegistration',
    data,
  });
  if (response) return response.data;
};

export const confirmUserPasswordRecovery = async (data: any) => {
  const response = await requestHandlerPublic({
    method: 'post',
    url: 'public/auth/confirmPasswordRecovery',
    data,
  });
  if (response) return response.data;
};

export const updatePasswordRecovery = async (data: any) => {
  const response = await requestHandlerPublic({
    method: 'post',
    url: 'public/auth/confirmPasswordRecovery/updatePassword',
    data,
  });
  if (response) return response.data;
};

export const registerUser = async (data: any) => {
  try {
    const response = await api.post('public/auth/register', data);
    return response.data;
  } catch (error: any) {
    if (error.response && error.response.status === 401) {
      return false;
    } else {
      throw error;
    }
  }
};

export const logoutUser = async (dispatch: Dispatch) => {
  try {
    const response = await api.post('internal/auth/logout');
    return response.data;
  } catch (error) {
    console.error(error);
  }
};

type RequestHandlerParams = {
  method: string;
  url: string;
  dispatch?: Dispatch;
  data?: any;
  headers?: Record<string, string>;
  responseType?: string;
  signal?: AbortSignal;
};

export const requestHandler = async ({
  method,
  url,
  dispatch,
  data,
  headers,
  responseType,
  signal,
}: RequestHandlerParams) => {
  try {
    const options: any = {
      method,
      url,
      data,
      headers,
      signal,
    };
    if (responseType) options.responseType = responseType;

    const response = await api.request(options);
    if (response.status === 205) {
      return window.location.reload();
    }
    return response;
  } catch (error: any) {
    if (error.response && error.response.status === 401) {
      // Venció el token
      if (dispatch) dispatch(logout());
      return false;
    } else if (error.response && error.response.status === 403) {
      // No tiene permisos
      alert('No tienes permisos para realizar esta acción');
      throw error;
    } else if (error.response && error.response.status === 500) {
      // Se cayó la app
      console.error(error);
      throw error;
    } else if (
      (error.response && error.response.status === 504) ||
      error.code === 'ETIMEDOUT'
    ) {
      // Server timeout
      console.error(error);
      throw new Error('TIMEOUT');
    } else {
      // Error mío
      console.error(error);
      throw error;
    }
  }
};

// My businesses

export const getBusinessRelationsForUser = async (dispatch: Dispatch) => {
  const response = await requestHandler({
    method: 'get',
    url: 'internal/users/businessRelations',
    dispatch,
  });
  if (response) return response.data;
};

export const getActiveBusinessRelationsForUser = async (dispatch: Dispatch) => {
  const response = await requestHandler({
    method: 'get',
    url: 'internal/users/businessRelations/active',
    dispatch,
  });
  if (response) return response.data;
};

export const getBusinessRelation = async (
  dispatch: Dispatch,
  businessId: string,
) => {
  const response = await requestHandler({
    method: 'get',
    url: `internal/users/businessRelations/${businessId}`,
    dispatch,
  });
  if (response) return response.data;
};

export const getPausedBusinessRelations = async (dispatch: Dispatch) => {
  const response = await requestHandler({
    method: 'get',
    url: `internal/users/businessRelations/paused`,
    dispatch,
  });
  if (response) return response.data;
};

export const getBusinessCessionsSummary = async (
  dispatch: Dispatch,
  id: string,
) => {
  const response = await requestHandler({
    method: 'get',
    url: `internal/business/${id}/cessions/summary`,
    dispatch,
  });
  if (response) return response.data;
};

export const getBusinessF29Summary = async (dispatch: Dispatch, id: string) => {
  const response = await requestHandler({
    method: 'get',
    url: `internal/business/${id}/f29/summary`,
    dispatch,
  });
  if (response) return response.data;
};
export const getBusinessF22Summary = async (dispatch: Dispatch, id: string) => {
  const response = await requestHandler({
    method: 'get',
    url: `internal/business/${id}/f22/summary`,
    dispatch,
  });
  if (response) return response.data;
};

export const getBusinessBankruptcyPosts = async (
  dispatch: Dispatch,
  id: string,
) => {
  const response = await requestHandler({
    method: 'get',
    url: `internal/business/${id}/bankruptcy`,
    dispatch,
  });
  if (response) return response.data;
};

export const getBusinessTgr = async (dispatch: Dispatch, id: string) => {
  const response = await requestHandler({
    method: 'get',
    url: `internal/business/${id}/tgr`,
    dispatch,
  });
  if (response) return response.data;
};

export const getBusinessTgrProceedings = async (
  dispatch: Dispatch,
  id: string,
): Promise<BusinessProceedingsResponse | undefined> => {
  const response = await requestHandler({
    method: 'get',
    url: `internal/business/${id}/tgr/proceedings`,
    dispatch,
  });
  if (response) return response.data;
};

export const getBusinessTgDebt = async (
  dispatch: Dispatch,
  id: string,
  query: string,
) => {
  const response = await requestHandler({
    method: 'get',
    url: `internal/business/${id}/tgr/debts/certificate?${query}`,
    dispatch,
  });
  if (response) return response.data;
};

export const getDocumentsAvailablesForFactoring = async (
  dispatch: Dispatch,
  id: string,
  subProductTypeId: string,
  query: string,
) => {
  const response = await requestHandler({
    method: 'get',
    url: `internal/business/${id}/documents/availables/subproduct/${subProductTypeId}?${query}`,
    dispatch,
  });
  if (response) return response.data;
};

export const getBusinessCrossInvoicesSummary = async (
  dispatch: Dispatch,
  id: string,
) => {
  const response = await requestHandler({
    method: 'get',
    url: `internal/business/${id}/crossInvoices/summary`,
    dispatch,
  });
  if (response) return response.data;
};

export const getBusinessInvoicesByDateRange = async (
  dispatch: Dispatch,
  id: string,
  startDate: string,
  endDate: string,
) => {
  const response = await requestHandler({
    method: 'post',
    url: `internal/business/${id}/invoices/dateRange`,
    dispatch,
    data: { startDate, endDate },
  });
  if (response) return response.data;
};

export const getBusiness = async (dispatch: Dispatch, id: string) => {
  const response = await requestHandler({
    method: 'get',
    url: `internal/business/${id}`,
    dispatch,
  });
  if (response) return response.data;
};

// IVA
export const dumpIvaFolder = async (
  dispatch: Dispatch,
  businessId: string,
  pdfBase64: string,
) => {
  const response = await requestHandler({
    method: 'post',
    url: `internal/business/${businessId}/iva/dumpIvaFolder`,
    data: pdfBase64,
    headers: {
      'Content-Type': 'text/plain',
    },
    dispatch,
  });
  if (response) return response.data;
};

// Last invoices

export const getInvoicesWithStatus = async (dispatch: Dispatch) => {
  const response = await requestHandler({
    method: 'get',
    url: `internal/users/invoices`,
    dispatch,
  });
  if (response) return response.data;
};

// Enrolation flow
export const createBusiness = async (dispatch: Dispatch, data: any) => {
  const response = await requestHandler({
    method: 'post',
    url: `internal/business`,
    dispatch,
    data,
  });
  if (response) return response.data;
};

export const startEnrolation = async (dispatch: Dispatch, data: any) => {
  const response = await requestHandler({
    method: 'post',
    url: `internal/enrollment/start`,
    dispatch,
    data,
  });
  if (response) return response.data;
};

export const validateCredentials = async (dispatch: Dispatch, data: any) => {
  const response = await requestHandler({
    method: 'post',
    url: `internal/enrollment/validateCredentials`,
    dispatch,
    data,
  });
  if (response) return response.data;
};

// Admin Routes
export const getCompanyUsers = async (dispatch: Dispatch) => {
  const response = await requestHandler({
    method: 'get',
    url: `internal/admin/users`,
    dispatch,
  });
  if (response) return response.data;
};

export const getOtherCompanyUsers = async (
  dispatch: Dispatch,
  businessIdentifier: string,
) => {
  const response = await requestHandler({
    method: 'get',
    url: `internal/admin/users/otherCompany/${businessIdentifier}`,
    dispatch,
  });
  if (response) return response.data;
};

export const getUsersRelations = async (dispatch: Dispatch, data: any) => {
  const response = await requestHandler({
    method: 'get',
    url: `internal/admin/users/${data.id}/businessRelations`,
    dispatch,
  });
  if (response) return response.data;
};

export const createUsersRelations = async (dispatch: Dispatch, data: any) => {
  const response = await requestHandler({
    method: 'post',
    url: `internal/admin/users/${data.id}/businessRelations`,
    dispatch,
    data,
  });
  if (response) return response.data;
};

export const deleteUsersRelations = async (dispatch: Dispatch, data: any) => {
  const response = await requestHandler({
    method: 'delete',
    url: `internal/admin/users/${data.id}/businessRelations`,
    dispatch,
    data,
  });
  if (response) return response.data;
};

export const getUserCollectionPortfolio = async (
  dispatch: Dispatch,
  id: number,
) => {
  const response = await requestHandler({
    method: 'get',
    url: `internal/admin/users/${id}/collectionPortfolio`,
    dispatch,
  });
  if (response) return response.data;
};

export const transferCollectionPortfolio = async (
  dispatch: Dispatch,
  data: any,
) => {
  const response = await requestHandler({
    method: 'post',
    url: `internal/admin/users/collectionPortfolio/transfer`,
    dispatch,
    data,
  });
  if (response) return response.data;
};

export const createUsersBusinessesPortfolio = async (
  dispatch: Dispatch,
  id: number,
  data: any,
) => {
  const response = await requestHandler({
    method: 'post',
    url: `internal/admin/users/${id}/collectionPortfolio`,
    dispatch,
    data,
  });
  if (response) return response.data;
};

export const deleteUserBusinessPortfolio = async (
  dispatch: Dispatch,
  id: number,
  data: any,
) => {
  const response = await requestHandler({
    method: 'delete',
    url: `internal/admin/users/${id}/collectionPortfolio`,
    dispatch,
    data,
  });
  if (response) return response.data;
  return true;
};

export const createUser = async (dispatch: Dispatch, data: any) => {
  const response = await requestHandler({
    method: 'post',
    url: `internal/admin/users`,
    dispatch,
    data,
  });
  if (response) return response.data;
};

export const resendUserEmail = async (dispatch: Dispatch, data: any) => {
  const response = await requestHandler({
    method: 'post',
    url: `internal/admin/users/${data.userId}/reSendEmail`,
    dispatch,
    data,
  });
  if (response) return response.data;
};

export const updateUser = async (dispatch: Dispatch, data: any) => {
  const response = await requestHandler({
    method: 'patch',
    url: `internal/admin/users/${data.id}`,
    dispatch,
    data,
  });
  if (response) return response.data;
};

export const deleteUser = async (dispatch: Dispatch, data: any) => {
  const response = await requestHandler({
    method: 'delete',
    url: `internal/admin/users/${data.id}`,
    dispatch,
    data,
  });
  if (response) return response.data;
};

// User Profile
export const getUserEncryptedId = async (dispatch: Dispatch, data: any) => {
  const response = await requestHandler({
    method: 'get',
    url: `internal/users/${data.id}/generateEcryptedId`,
    dispatch,
  });
  if (response) return response.data;
};

export const getUser = async (dispatch: Dispatch, data: any) => {
  const response = await requestHandler({
    method: 'get',
    url: `internal/users/${data.id}`,
    dispatch,
  });
  if (response) return response.data;
};

// Public enrolation Routes

export const getUserByEncryptedId = async (data: any) => {
  const response = await requestHandler({
    method: 'get',
    url: `public/users/encrypted/${data.id}/${data.version}`,
  });
  if (response) return response.data;
};

export const createBusinessPublic = async (data: any) => {
  const response = await requestHandler({
    method: 'post',
    url: `public/business?id=${data.id}&version=${data.version}`,
    data,
  });
  if (response) return response.data;
};

export const startEnrolationPublic = async (data: any) => {
  const response = await requestHandler({
    method: 'post',
    url: `public/enrollment/start?id=${data.id}&version=${data.version}`,
    data,
  });
  if (response) return response.data;
};

export const validateCredentialsPublic = async (data: any) => {
  const response = await requestHandler({
    method: 'post',
    url: `public/enrollment/validateCredentials?id=${data.id}&version=${data.version}`,
    data,
  });
  if (response) return response.data;
};

// Financial Statemetn habdling

export const addFinancialStatementNode = async (
  dispatch: Dispatch,
  data: any,
) => {
  const response = await requestHandler({
    method: 'post',
    url: `internal/financialStatements/nodes`,
    dispatch,
    data,
  });
  if (response) return response.data;
};

export const editFinancialStatementNode = async (
  dispatch: Dispatch,
  data: any,
) => {
  const response = await requestHandler({
    method: 'patch',
    url: `internal/financialStatements/nodes`,
    dispatch,
    data,
  });
  if (response) return response.data;
};

// Factoring

export const getAllFactorings = async (dispatch: Dispatch) => {
  const response = await requestHandler({
    method: 'get',
    url: `internal/factoring/companies`,
    dispatch,
  });
  if (response) return response.data;
};

export const getAllSiiViews = async (dispatch: Dispatch) => {
  const response = await requestHandler({
    method: 'get',
    url: `internal/invoices/getSiiViews`,
    dispatch,
  });
  if (response) return response.data;
};

export const getFactoringRequestsReceived = async (
  dispatch: Dispatch,
  query?: string,
) => {
  const response = await requestHandler({
    method: 'get',
    url: `internal/users/factoring/reciever/requests?${query}`,
    dispatch,
  });
  if (response) return response.data;
};

export const getFactoringRequestsReceivedToApprove = async (
  dispatch: Dispatch,
) => {
  const response = await requestHandler({
    method: 'get',
    url: `internal/users/factoring/reciever/requestsToApprove`,
    dispatch,
  });
  if (response) return response.data;
};

export const getFactoringRequestsSent = async (
  dispatch: Dispatch,
  companyRecieverId?: string,
) => {
  const response = await requestHandler({
    method: 'get',
    url: `internal/users/factoring/sender/request?companyRecieverId=${companyRecieverId}`,
    dispatch,
  });
  if (response) return response.data;
};

export const getAllFactoringQuotesSentFiltered = async (
  dispatch: Dispatch,
  companyId: string,
) => {
  const response = await requestHandler({
    method: 'get',
    url: `internal/users/factoring/sender/quotes?companyId=${companyId}`,
    dispatch,
  });
  if (response) return response.data;
};

export const getFactoringRequestSentWithQuotesSummary = async (
  dispatch: Dispatch,
  data: any,
) => {
  const response = await requestHandler({
    method: 'get',
    url: `internal/users/factoring/sender/request/${data.id}`,
    dispatch,
  });
  if (response) return response.data;
};

export const getSenderFactoringRequest = async (
  dispatch: Dispatch,
  data: any,
) => {
  const response = await requestHandler({
    method: 'get',
    url: `internal/users/factoring/sender/request/${data.requestId}`,
    dispatch,
  });
  if (response) return response.data;
};

export const getSenderFactoringQuote = async (
  dispatch: Dispatch,
  data: any,
) => {
  const response = await requestHandler({
    method: 'get',
    url: `internal/users/factoring/sender/request/${data.requestId}/quotes/${data.quoteId}`,
    dispatch,
  });
  if (response) return response.data;
};

export const getRecieverFactoringRequestOnlyQuote = async (
  dispatch: Dispatch,
  data: any,
) => {
  const response = await requestHandler({
    method: 'get',
    url: `internal/users/factoring/reciever/requests/${data.requestId}/onlyQuote`,
    dispatch,
  });
  if (response) return response.data;
};

export const getRecieverFactoringRequest = async (
  dispatch: Dispatch,
  data: any,
) => {
  const response = await requestHandler({
    method: 'get',
    url: `internal/users/factoring/reciever/requests/${data.requestId}`,
    dispatch,
  });
  if (response) return response.data;
};

export const getFactoringQuoteEvaluations = async (
  dispatch: Dispatch,
  data: any,
) => {
  const response = await requestHandler({
    method: 'get',
    url: `internal/users/factoring/reciever/quotes/${data.quoteId}`,
    dispatch,
  });
  if (response) return response.data;
};

export const getRecieverFactoringQuote = async (
  dispatch: Dispatch,
  data: any,
) => {
  const response = await requestHandler({
    method: 'get',
    url: `internal/users/factoring/reciever/quotes/${data.quoteId}`,
    dispatch,
  });
  if (response) return response.data;
};

export const updateFactoringRequest = async (dispatch: Dispatch, data: any) => {
  const response = await requestHandler({
    method: 'patch',
    url: `internal/factoring/request`,
    data,
    dispatch,
  });
  if (response) return response.data;
};

export const getTemporaryBusinessRelations = async (dispatch: Dispatch) => {
  const response = await requestHandler({
    method: 'get',
    url: `internal/users/temporaryBusinessRelations`,
    dispatch,
  });
  if (response) return response.data;
};

export const getPendingFactoringRequest = async (dispatch: Dispatch) => {
  const response = await requestHandler({
    method: 'get',
    url: `internal/users/factoring/pendingRequests`,
    dispatch,
  });
  if (response) return response.data;
};

export const getFactoringRequestExtraData = async (
  dispatch: Dispatch,
  data: any,
) => {
  const response = await requestHandler({
    method: 'get',
    url: `internal/factoring/request/${data.id}/extraData`,
    dispatch,
    responseType: 'blob',
  });
  return response;
};

export const getTemporaryBusinessViewsForExecutive = async (
  dispatch: Dispatch,
  id: string,
) => {
  const response = await requestHandler({
    method: 'get',
    url: `internal/users/reciever/temporaryViews/business/${id}`,
    dispatch,
  });
  if (response) return response.data;
};

export const getTemporaryBusinessForExecutives = async (dispatch: Dispatch) => {
  const response = await requestHandler({
    method: 'get',
    url: `internal/users/reciever/temporaryViews`,
    dispatch,
  });
  if (response) return response.data;
};

export const gettUserExecutives = async (dispatch: Dispatch) => {
  const response = await requestHandler({
    method: 'get',
    url: `internal/users/executives`,
    dispatch,
  });
  if (response) return response.data;
};

export const getTemporaryConnectionsUserProviderBusiness = async (
  dispatch: Dispatch,
  businessId: string,
) => {
  const response = await requestHandler({
    method: 'get',
    url: `internal/users/provider/temporaryViews/business/${businessId}`,
    dispatch,
  });
  if (response) return response.data;
};

export const createTemporaryView = async (dispatch: Dispatch, data: any) => {
  const response = await requestHandler({
    method: 'post',
    url: `internal/users/provider/temporaryViews/business/${data.businessId}`,
    data,
    dispatch,
  });
  if (response) return response.data;
};

export const deleteTemporaryView = async (
  dispatch: Dispatch,
  businessId: string,
  data: any,
) => {
  const response = await requestHandler({
    method: 'delete',
    url: `internal/users/provider/temporaryViews/business/${businessId}/user/${data.recipientId}`,
    dispatch,
  });
  if (response) return response.data;
};

export const editTemporaryView = async (dispatch: Dispatch, data: any) => {
  const response = await requestHandler({
    method: 'patch',
    url: `internal/users/provider/temporaryViews/business/${data.businessId}/user/${data.recipientId}`,
    data,
    dispatch,
  });
  if (response) return response.data;
};

// Files

export const getFilesFromFactoringRequest = async (
  dispatch: Dispatch,
  data: any,
) => {
  const response = await requestHandler({
    method: 'get',
    url: `internal/factoring/quote/${data.id}/files`,
    dispatch,
  });
  if (response) return response.data;
};

export const downloadFileFromFactoringRequests = async (
  dispatch: Dispatch,
  data: any,
) => {
  const response = await requestHandler({
    method: 'get',
    url: `internal/factoring/quote/${data.id}/files/download?fileName=${data.fileName}`,
    dispatch,
  });
  if (response) return response.data;
};

export const uploadFilesToQuote = async (
  dispatch: Dispatch,
  data: any,
  id: number,
) => {
  const headers = {
    'Content-Type': 'multipart/form-data',
  };
  const response = await requestHandler({
    method: 'patch',
    url: `internal/factoring/quote/${id}/files`,
    data,
    headers,
    dispatch,
  });
  if (response) return response.data;
};

export const isAFactoringCompany = async (dispatch: Dispatch, id: string) => {
  const response = await requestHandler({
    method: 'get',
    url: `public/auth/company/${id}/isFactoring`,
    dispatch,
  });
  if (response) return response.data;
};

export const getAssistantResponseADC = async (
  dispatch: Dispatch,
  data: any,
) => {
  const response = await requestHandler({
    method: 'post',
    url: `internal/users/assistant/adc/getResponse`,
    data,
    dispatch,
  });
  if (response) return response.data;
};

export const getScoringSheets = async (dispatch: Dispatch) => {
  const response = await requestHandler({
    method: 'get',
    url: `internal/scoring/availableSheets`,
    dispatch,
  });
  if (response) return response.data;
};

export const getScoringMatrix = async (dispatch: Dispatch) => {
  const response = await requestHandler({
    method: 'get',
    url: `internal/scoring/ratingMatrix`,
    dispatch,
  });
  if (response) return response.data;
};

export const createSheetAssignation = async (dispatch: Dispatch, data: any) => {
  const response = await requestHandler({
    method: 'post',
    url: `internal/scoring/assignations`,
    data,
    dispatch,
  });
  if (response) return response.data;
};

export const editSheetAssignation = async (dispatch: Dispatch, data: any) => {
  const response = await requestHandler({
    method: 'patch',
    url: `internal/scoring/assignations`,
    data,
    dispatch,
  });
  if (response) return response.data;
};

export const getScoringAssginations = async (
  dispatch: Dispatch,
  businessId: string,
) => {
  const response = await requestHandler({
    method: 'get',
    url: `internal/business/${businessId}/assignations`,
    dispatch,
  });
  if (response) return response.data;
};

export const getScoringAssgination = async (dispatch: Dispatch, data: any) => {
  const response = await requestHandler({
    method: 'get',
    url: `internal/scoring/assignations/${data.id}`,
    dispatch,
  });
  if (response) return response.data;
};
export const selectAssignationSheet = async (dispatch: Dispatch, data: any) => {
  const response = await requestHandler({
    method: 'post',
    url: `internal/scoring/assignations/select`,
    data,
    dispatch,
  });
  if (response) return response.data;
};

export const getConsultorResponse = async (dispatch: Dispatch, data: any) => {
  const response = await requestHandler({
    method: 'post',
    url: `internal/consultor/orchestrator`,
    data,
    dispatch,
  });
  if (response) return response.data;
};

export const getNodesForDecisionAvailables = async (
  dispatch: Dispatch,
  data: any,
) => {
  const response = await requestHandler({
    method: 'get',
    url: `internal/decisionNode/availableNodes?revisionObjective=${data.revisionObjective}`,
    dispatch,
  });
  if (response) return response.data;
};

export const saveDecisionTree = async (dispatch: Dispatch, data: any) => {
  const response = await requestHandler({
    method: 'post',
    url: `internal/decisionEngine/save`,
    data,
    dispatch,
  });
  if (response) return response.data;
};

export const getAllDecisionEngines = async (dispatch: Dispatch) => {
  const response = await requestHandler({
    method: 'get',
    url: `internal/decisionEngine`,
    dispatch,
  });
  if (response) return response.data;
};

export const getAllFinalNodes = async (dispatch: Dispatch) => {
  const response = await requestHandler({
    method: 'get',
    url: `internal/decisionNode/finalNodes`,
    dispatch,
  });
  if (response) return response.data;
};

export const getDecisionEngineAssignations = async (
  dispatch: Dispatch,
  id: string,
) => {
  const response = await requestHandler({
    method: 'get',
    url: `internal/decisionEngine/${id}`,
    dispatch,
  });
  if (response) return response.data;
};

export const updateDecisionEngine = async (dispatch: Dispatch, data: any) => {
  const response = await requestHandler({
    method: 'put',
    url: `internal/decisionEngine/${data.id}/update`,
    data,
    dispatch,
  });
  if (response) return response.data;
};

export const getSelectedDecisionEngine = async (
  dispatch: Dispatch,
  data: any,
) => {
  const response = await requestHandler({
    method: 'get',
    url: `internal/decisionEngine/selected?revisionObjective=${data.revisionObjective}`,
    dispatch,
  });
  if (response) return response.data;
};

export const getCompanySelectedDecisionEngine = async (
  dispatch: Dispatch,
  data: any,
) => {
  const response = await requestHandler({
    method: 'get',
    url: `internal/company/${data.id}/decisionEngine/selected`,
    dispatch,
  });
  if (response) return response.data;
};

export const saveDecisionEngineAssignation = async (
  dispatch: Dispatch,
  data: any,
) => {
  const response = await requestHandler({
    method: 'post',
    url: `internal/decisionEngine/execute/saveResults`,
    data,
    dispatch,
  });
  if (response) return response.data;
};

export const getWhitelists = async (dispatch: Dispatch) => {
  const response = await requestHandler({
    method: 'get',
    url: `internal/whitelist`,
    dispatch,
  });
  if (response) return response.data;
};

export const createWhitelist = async (dispatch: Dispatch, data: any) => {
  const response = await requestHandler({
    method: 'post',
    url: `internal/whitelist`,
    data,
    dispatch,
  });
  if (response) return response.data;
};

export const deleteWhitelist = async (dispatch: Dispatch, data: any) => {
  const response = await requestHandler({
    method: 'delete',
    url: `internal/whitelist/${data.id}`,
    dispatch,
  });
  if (response) return response.data;
};

export const getNotifications = async (
  dispatch: Dispatch,
  paginationKey: string | null,
) => {
  const response = await requestHandler({
    method: 'get',
    url: `internal/users/notifications?paginationKey=${paginationKey}`,
    dispatch,
  });
  if (response) return response.data;
};

export const markNotificationsAsRead = async (
  dispatch: Dispatch,
  data: any,
) => {
  const response = await requestHandler({
    method: 'patch',
    url: `internal/users/notifications/read`,
    data,
    dispatch,
  });
  if (response) return response.data;
};

export const getUnReadNotifications = async (dispatch: Dispatch) => {
  const response = await requestHandler({
    method: 'get',
    url: `internal/users/notifications/unRead`,
    dispatch,
  });
  if (response) return response.data;
};

export const getOwnCompanyProducts = async (dispatch: Dispatch) => {
  const response = await requestHandler({
    method: 'get',
    url: `internal/product`,
    dispatch,
  });
  if (response) return response.data;
};

export const getReceiverCompanyProducts = async (dispatch: Dispatch) => {
  const response = await requestHandler({
    method: 'get',
    url: `internal/product/receiver`,
    dispatch,
  });
  if (response) return response.data;
};

export const createProduct = async (dispatch: Dispatch, data: any) => {
  const response = await requestHandler({
    method: 'post',
    url: `internal/product`,
    data,
    dispatch,
  });
  if (response) return response.data;
};

export const getProduct = async (dispatch: Dispatch, data: any) => {
  const response = await requestHandler({
    method: 'get',
    url: `internal/product/${data.id}`,
    dispatch,
  });
  if (response) return response.data;
};

export const getAvailableProducts = async (dispatch: Dispatch) => {
  const response = await requestHandler({
    method: 'get',
    url: `internal/product/available`,
    dispatch,
  });
  if (response) return response.data;
};

export const deleteProduct = async (dispatch: Dispatch, data: any) => {
  const response = await requestHandler({
    method: 'delete',
    url: `internal/product/${data.id}`,
    dispatch,
  });
  if (response) return response.data;
};

export const updateProduct = async (dispatch: Dispatch, data: any) => {
  const response = await requestHandler({
    method: 'patch',
    url: `internal/product/${data.id}`,
    data,
    dispatch,
  });
  if (response) return response.data;
};

// CreditLine

export const getCreditLinesRequested = async (
  dispatch: Dispatch,
  clientId?: string,
) => {
  const response = await requestHandler({
    method: 'get',
    url: `internal/creditLine/receiver?clientId=${clientId}`,
    dispatch,
  });
  if (response) return response.data;
};

export const updateFactoringFileName = async (
  dispatch: Dispatch,
  data: any,
) => {
  const response = await requestHandler({
    method: 'patch',
    url: `internal/factoring/quote/${data.id}/files`,
    data,
    dispatch,
  });
  if (response) return response.data;
};

export const getRequirementsForProduct = async (
  dispatch: Dispatch,
  data: any,
) => {
  const response = await requestHandler({
    method: 'get',
    url: `internal/creditLine/${data.creditLineId}/requirements`,
    dispatch,
  });
  if (response) return response.data;
};

export const createCreditLine = async (dispatch: Dispatch, data: any) => {
  const response = await requestHandler({
    method: 'post',
    url: `internal/creditLine`,
    data,
    dispatch,
  });
  if (response) return response.data;
};

export const requestCreditLine = async (dispatch: Dispatch, data: any) => {
  const response = await requestHandler({
    method: 'post',
    url: `internal/creditLine/${data.id}/request`,
    data,
    dispatch,
  });
  if (response) return response.data;
};

export const requestMoreInformationCreditLine = async (
  dispatch: Dispatch,
  data: any,
) => {
  const response = await requestHandler({
    method: 'post',
    url: `internal/creditLine/${data.id}/requestMoreInformation`,
    data,
    dispatch,
  });
  if (response) return response.data;
};

export const requestCreditLineValidated = async (
  dispatch: Dispatch,
  data: any,
) => {
  const response = await requestHandler({
    method: 'patch',
    url: `internal/creditLine/${data.id}/validate`,
    data,
    dispatch,
  });
  if (response) return response.data;
};

export const getConcentrations = async (dispatch: Dispatch, data: any) => {
  const response = await requestHandler({
    method: 'get',
    url: `internal/creditLine/${data.id}/concentrations`,
    dispatch,
  });
  if (response) return response.data;
};

export const getCreditLineForReceiver = async (
  dispatch: Dispatch,
  data: any,
) => {
  const response = await requestHandler({
    method: 'get',
    url: `internal/creditLine/receiver/${data.id}`,
    dispatch,
  });
  if (response) return response.data;
};

export const getCreditLineForGiver = async (dispatch: Dispatch, data: any) => {
  const response = await requestHandler({
    method: 'get',
    url: `internal/creditLine/giver/${data.id}`,
    dispatch,
  });
  if (response) return response.data;
};

export const getCreditLinesForGiver = async (
  dispatch: Dispatch,
  query: string,
) => {
  const response = await requestHandler({
    method: 'get',
    url: `internal/creditLine/giver?${query}`,
    dispatch,
  });
  if (response) return response.data;
};

export const getBusinessPayers = async (dispatch: Dispatch, id: string) => {
  const response = await requestHandler({
    method: 'get',
    url: `internal/business/${id}/payers`,
    dispatch,
  });
  if (response) return response.data;
};

export const getProductSubProducts = async (dispatch: Dispatch, id: number) => {
  const response = await requestHandler({
    method: 'get',
    url: `internal/creditLine/${id}/subProducts`,
    dispatch,
  });
  if (response) return response.data;
};

export const getRequestEvaluations = async (
  dispatch: Dispatch,
  data: any,
): Promise<Record<string, Evaluation[]>> => {
  const response = await requestHandler({
    method: 'get',
    url: `internal/decisionEngine/requestType/${data.requestType}/requestId/${data.requestId}`,
    dispatch,
  });
  if (response) return response.data;
  return {};
};

export const reviewCreditLine = async (dispatch: Dispatch, data: any) => {
  const response = await requestHandler({
    method: 'post',
    url: `internal/creditLine/${data.id}/review`,
    data,
    dispatch,
  });
  if (response) return response.data;
};

export const rejectCreditLine = async (dispatch: Dispatch, data: any) => {
  const response = await requestHandler({
    method: 'post',
    url: `internal/creditLine/${data.id}/reject`,
    data,
    dispatch,
  });
  if (response) return response.data;
};

export const saveConcentrations = async (dispatch: Dispatch, data: any) => {
  const response = await requestHandler({
    method: 'patch',
    url: `internal/creditLine/${data.id}/concentrations`,
    data,
    dispatch,
  });
  if (response) return response.data;
};

export const assginCreditLine = async (dispatch: Dispatch, data: any) => {
  const response = await requestHandler({
    method: 'patch',
    url: `internal/creditLine/${data.id}/assign`,
    data,
    dispatch,
  });
  if (response) return response.data;
};

export const getCreditLineAssignations = async (
  dispatch: Dispatch,
  data: any,
) => {
  const response = await requestHandler({
    method: 'get',
    url: `internal/creditLine/${data.id}/assignations`,
    dispatch,
  });
  if (response) return response.data;
};

export const getCreditLineDetails = async (dispatch: Dispatch, data: any) => {
  const response = await requestHandler({
    method: 'get',
    url: `internal/creditLine/giver/${data.creditLineId}/details`,
    dispatch,
  });
  if (response) return response.data;
};

export const getAvailableClientsProductsForCreditLine = async (
  dispatch: Dispatch,
) => {
  const response = await requestHandler({
    method: 'get',
    url: `internal/company/availableClientsProducts`,
    dispatch,
  });
  if (response) return response.data;
};

export const freezeCreditLine = async (dispatch: Dispatch, data: any) => {
  const response = await requestHandler({
    method: 'patch',
    url: `internal/creditLine/${data.id}/freeze`,
    data,
    dispatch,
  });
  if (response) return response.data;
};

export const unFreezeCreditLine = async (dispatch: Dispatch, data: any) => {
  const response = await requestHandler({
    method: 'patch',
    url: `internal/creditLine/${data.id}/unfreeze`,
    data,
    dispatch,
  });
  if (response) return response.data;
};

export const postCollectionManagement = async (
  dispatch: Dispatch,
  data: {
    description: string;
    optionId: number;
    type: string;
    documentId: string;
    state: string;
    dueDate: string;
    completedAt: string | null;
    executiveId: number;
    possiblePaymentDate: string;
  },
) => {
  const response = await requestHandler({
    method: 'post',
    url: `internal/collection/`,
    data,
    dispatch,
  });
  if (response) return response.data;
};

export const getCollectionManagementsByCompanyId = async (
  dispatch: Dispatch,
  query: string,
) => {
  const response = await requestHandler({
    method: 'get',
    url: `internal/collection?${query}`,
    dispatch,
  });
  if (response) return response.data;
};

export const deleteCollectionManagementById = async (
  dispatch: Dispatch,
  id: number,
) => {
  const response = await requestHandler({
    method: 'delete',
    url: `internal/collection/${id}`,
    dispatch,
  });
  if (response) return response.data;
};

export const createDocumentsFromCompany = async (
  dispatch: Dispatch,
  data: any,
) => {
  const response = await requestHandler({
    method: 'post',
    url: `internal/company/createDocuments`,
    data,
    dispatch,
  });
  if (response) return response.data;
};

export const getComitees = async (dispatch: Dispatch) => {
  const response = await requestHandler({
    method: 'get',
    url: `internal/comitee`,
    dispatch,
  });
  if (response) return response.data;
};

export const createComitee = async (dispatch: Dispatch, data: any) => {
  const response = await requestHandler({
    method: 'post',
    url: `internal/comitee`,
    data,
    dispatch,
  });
  if (response) return response.data;
};

export const getComitee = async (dispatch: Dispatch, data: any) => {
  const response = await requestHandler({
    method: 'get',
    url: `internal/comitee/${data.id}`,
    dispatch,
  });
  if (response) return response.data;
};

export const deleteComitee = async (dispatch: Dispatch, data: any) => {
  const response = await requestHandler({
    method: 'delete',
    url: `internal/comitee/${data.id}`,
    dispatch,
  });
  if (response) return response.data;
};

export const updateComitee = async (dispatch: Dispatch, data: any) => {
  const response = await requestHandler({
    method: 'patch',
    url: `internal/comitee/${data.id}`,
    data,
    dispatch,
  });
  if (response) return response.data;
};

export const createDecisionNode = async (dispatch: Dispatch, data: any) => {
  const response = await requestHandler({
    method: 'post',
    url: `internal/decisionNode`,
    data,
    dispatch,
  });
  if (response) return response.data;
};

export const getDecisionNode = async (dispatch: Dispatch, data: any) => {
  const response = await requestHandler({
    method: 'get',
    url: `internal/decisionNode/${data.id}`,
    dispatch,
  });
  if (response) return response.data;
};

export const deleteDecisionNode = async (dispatch: Dispatch, data: any) => {
  const response = await requestHandler({
    method: 'delete',
    url: `internal/decisionNode/${data.id}`,
    dispatch,
  });
  if (response) return response.data;
};

export const updateDecisionNode = async (dispatch: Dispatch, data: any) => {
  const response = await requestHandler({
    method: 'patch',
    url: `internal/decisionNode/${data.id}`,
    data,
    dispatch,
  });
  if (response) return response.data;
};

export const getMissingFactoringApprovals = async (
  dispatch: Dispatch,
  data: any,
) => {
  const response = await requestHandler({
    method: 'get',
    url: `internal/factoring/quote/${data.id}/missingApprovals`,
    dispatch,
  });
  if (response) return response.data;
};

export const syncInvoicesCessionWithSII = async (
  dispatch: Dispatch,
  data: any,
) => {
  const response = await requestHandler({
    method: 'post',
    url: `internal/factoring/quote/${data.quoteId}/checkCessionAvailable`,
    data,
    dispatch,
  });
  if (response) return response.data;
};

export const getBusinessSummary = async (
  dispatch: Dispatch,
  businessId: string,
) => {
  const response = await requestHandler({
    method: 'get',
    url: `internal/business/${businessId}/open/summary`,
    dispatch,
  });
  if (response) return response.data;
};

export const updateBusinessSummary = async (
  dispatch: Dispatch,
  businessId: string,
  data: any,
) => {
  const response = await requestHandler({
    method: 'patch',
    url: `internal/business/${businessId}/open/summary`,
    data,
    dispatch,
  });
  if (response) return response.data;
};

export const updateBusinessName = async (dispatch: Dispatch, data: any) => {
  const response = await requestHandler({
    method: 'patch',
    url: `internal/business/${data.identifier}/name`,
    data,
    dispatch,
  });
  if (response) return response.data;
};

export const getBusinessByIdentifier = async (
  dispatch: Dispatch,
  data: any,
) => {
  const response = await requestHandler({
    method: 'get',
    url: `internal/business/identifier/${data.identifier}`,
    dispatch,
  });
  if (response) return response.data;
};

export const getFactoringRequestBankAccounts = async (
  dispatch: Dispatch,
  data: any,
) => {
  const response = await requestHandler({
    method: 'get',
    url: `internal/factoring/request/${data.id}/bankAccounts`,
    dispatch,
  });
  if (response) return response.data;
};

export const getFactoringPayments = async (dispatch: Dispatch, data: any) => {
  const response = await requestHandler({
    method: 'get',
    url: `internal/factoring/request/${data.id}/payments`,
    dispatch,
  });
  if (response) return response.data;
};

export const savePayments = async (dispatch: Dispatch, data: any) => {
  const response = await requestHandler({
    method: 'post',
    url: `internal/factoring/request/${data.id}/payments`,
    data,
    dispatch,
  });
  if (response) return response.data;
};

export const markPaymentAsExecuted = async (dispatch: Dispatch, data: any) => {
  const response = await requestHandler({
    method: 'patch',
    url: `internal/factoring/request/${data.id}/payments/execute`,
    data,
    dispatch,
  });
  if (response) return response.data;
};

export const getAllPayouts = async (dispatch: Dispatch, query: string) => {
  const response = await requestHandler({
    method: 'get',
    url: `internal/company/payouts?${query}`,
    dispatch,
  });
  if (response) return response.data;
};

export const getCompanyNetwork = async (dispatch: Dispatch, query: string) => {
  const response = await requestHandler({
    method: 'get',
    url: `internal/company/network?${query}`,
    dispatch,
  });
  if (response) return response.data;
};

export const getCompanySuppliers = async (
  dispatch: Dispatch,
  query: string,
) => {
  const response = await requestHandler({
    method: 'get',
    url: `internal/business/suppliers?${query}`,
    dispatch,
  });
  if (response) return response.data;
};

export const assignSupplierAccount = async (
  dispatch: Dispatch,
  supplierIdentifier: string,
  data: { financialStatementsTreeNodesId: number },
) => {
  const response = await requestHandler({
    method: 'post',
    url: `internal/business/suppliers/${supplierIdentifier}/accounts`,
    data: {
      financialStatementsTreeNodesId: data.financialStatementsTreeNodesId,
    },
    dispatch,
  });
  if (response) return response.data;
};

export const deleteSupplierAccount = async (
  dispatch: Dispatch,
  supplierIdentifier: string,
  accountId: number,
) => {
  const response = await requestHandler({
    method: 'delete',
    url: `internal/business/suppliers/${supplierIdentifier}/accounts/${accountId}`,
    dispatch,
  });
  if (response) return response.data;
};

export const getFactoringBalanceByPayers = async (
  dispatch: Dispatch,
  data: any,
) => {
  const response = await requestHandler({
    method: 'get',
    url: `internal/creditLine/balance/factoring/${data.identifier}/payers?productTypeId=factoring&quoteId=${data.quoteId}`,
    dispatch,
  });
  if (response) return response.data;
};

export const getQuoteAppliedDocuments = async (
  dispatch: Dispatch,
  data: any,
) => {
  const response = await requestHandler({
    method: 'get',
    url: `internal/conciliation/quote/${data.quoteId}/documentsApplied`,
    dispatch,
  });
  if (response) return response.data;
};

export const getAvailableSubProductsForFactoring = async (
  dispatch: Dispatch,
  businessId: string,
) => {
  const response = await requestHandler({
    method: 'get',
    url: `internal/business/${businessId}/factoring/availableSubProducts`,
    dispatch,
  });
  if (response) return response.data;
};

export const getSubProductConditions = async (
  dispatch: Dispatch,
  data: any,
) => {
  const response = await requestHandler({
    method: 'get',
    url: `internal/business/${data.businessId}/factoring/subProduct/${data.subProductId}/conditions`,
    dispatch,
  });
  if (response) return response.data;
};

export const getInvoicesByEncriptionBusinessIdentifier = async (
  encryptedCompanyIdentifier: string,
  encryptedBusinessIdentifier: string,
) => {
  const response = await requestHandler({
    method: 'get',
    url: `public/paymentLink/${encryptedCompanyIdentifier}/${encryptedBusinessIdentifier}`,
  });
  if (response) return response.data;
};

export const getInvoiceEncrypted = async (
  encryptedCompanyIdentifier: string,
  encryptedBusinessIdentifier: string,
  invoiceId: string,
) => {
  const response = await requestHandler({
    method: 'get',
    url: `public/paymentLink/invoice/${encryptedCompanyIdentifier}/${encryptedBusinessIdentifier}/${invoiceId}`,
  });
  if (response) return response.data;
};

export const createPaymentNotification = async (
  data: any,
  maskedCompanyId: string | undefined,
  maskedBusinessIdentifier: string | undefined,
) => {
  const response = await requestHandler({
    method: 'post',
    url: `public/paymentNotification/${maskedCompanyId}/${maskedBusinessIdentifier}`,
    data,
  });
  if (response) return response.data;
};

export const getBankAccount = async (
  encryptedCompanyId: string,
  encryptedBusinessIdentifier: string,
) => {
  const response = await requestHandler({
    method: 'get',
    url: `public/paymentLink/bankAccount/${encryptedCompanyId}/${encryptedBusinessIdentifier}`,
  });
  if (response) return response.data;
};

export const getPaymentNotifications = async (dispatch: Dispatch) => {
  const response = await requestHandler({
    method: 'get',
    url: `internal/paymentNotifications`,
    dispatch,
  });
  if (response) return response.data;
};

export const reviewPaymentNotification = async (
  dispatch: Dispatch,
  id: number,
) => {
  const response = await requestHandler({
    method: 'post',
    url: `internal/paymentNotifications/${id}/review`,
    dispatch,
  });
  if (response) return response.data;
};

export const unReviewPaymentNotification = async (
  dispatch: Dispatch,
  id: number,
) => {
  const response = await requestHandler({
    method: 'post',
    url: `internal/paymentNotifications/${id}/unreview`,
    dispatch,
  });
  if (response) return response.data;
};

export const automaticCollectionEnrollment = async (
  dispatch: Dispatch,
  data: any,
) => {
  const response = await requestHandler({
    method: 'post',
    url: `internal/collection/automaticCollection`,
    data,
    dispatch,
  });
  if (response) return response.data;
};

export const checkDocumentsPreselection = async (
  dispatch: Dispatch,
  data: any,
) => {
  const response = await requestHandler({
    method: 'post',
    url: `internal/factoring/preSelectionDocuments`,
    data,
    dispatch,
  });
  if (response) return response.data;
};

export const getPaymentNotificationByInvoice = async (
  dispatch: Dispatch,
  invoiceId: string,
) => {
  const response = await requestHandler({
    method: 'get',
    url: `internal/paymentNotifications/invoice/${invoiceId}`,
    dispatch,
  });
  if (response) return response.data;
};

export const createPendingBusinessAccount = async (
  dispatch: Dispatch,
  data: any,
) => {
  const response = await requestHandler({
    method: 'post',
    url: `internal/pendingAccount`,
    data,
    dispatch,
  });
  if (response) return response.data;
};

export const editPendingBusinessAccount = async (
  dispatch: Dispatch,
  pendingAccountId: number,
  data: any,
) => {
  const response = await requestHandler({
    method: 'patch',
    url: `internal/pendingAccount/${pendingAccountId}`,
    data,
    dispatch,
  });
  if (response) return response.data;
};

export const getBusinessPendingBusinessAccounts = async (
  dispatch: Dispatch,
  query: string,
) => {
  const response = await requestHandler({
    method: 'get',
    url: `internal/company/pendingAccount?${query}`,
    dispatch,
  });
  if (response) return response.data;
};

export const getPayableAccounts = async (dispatch: Dispatch, query: string) => {
  const response = await requestHandler({
    method: 'get',
    url: `internal/pendingAccount/payable?${query}`,
    dispatch,
  });
  if (response) return response.data;
};

export const getReceivableAccounts = async (
  dispatch: Dispatch,
  query: string,
) => {
  const response = await requestHandler({
    method: 'get',
    url: `internal/pendingAccount/receivable?${query}`,
    dispatch,
  });
  if (response) return response.data;
};

export const checkPendingAccountDeletion = async (
  dispatch: Dispatch,
  id: number,
) => {
  const response = await requestHandler({
    method: 'get',
    url: `internal/pendingAccount/${id}/checkDeletion`,
    dispatch,
  });
  if (response) return response.data;
};

export const deletePendingAccount = async (dispatch: Dispatch, id: number) => {
  const response = await requestHandler({
    method: 'delete',
    url: `internal/pendingAccount/${id}`,
    dispatch,
  });
  if (response) return response.data;
};

export const getPendingAccountsCategories = async (dispatch: Dispatch) => {
  const response = await requestHandler({
    method: 'get',
    url: `internal/pendingAccountCategory`,
    dispatch,
  });
  if (response) return response.data;
};

export const editPendingAccountsCategory = async (
  dispatch: Dispatch,
  id: number,
  data: any,
) => {
  const response = await requestHandler({
    method: 'patch',
    url: `internal/pendingAccountCategory/${id}`,
    data,
    dispatch,
  });
  if (response) return response.data;
};

export const createPendingAccountsCategory = async (
  dispatch: Dispatch,
  data: any,
) => {
  const response = await requestHandler({
    method: 'post',
    url: `internal/pendingAccountCategory`,
    data,
    dispatch,
  });
  if (response) return response.data;
};

export const deletePendingAccountsCategory = async (
  dispatch: Dispatch,
  id: number,
) => {
  const response = await requestHandler({
    method: 'delete',
    url: `internal/pendingAccountCategory/${id}`,
    dispatch,
  });
  if (response) return response.data;
};

export const getPendingAccountsCategory = async (
  dispatch: Dispatch,
  id: number,
) => {
  const response = await requestHandler({
    method: 'get',
    url: `internal/pendingAccountCategory/${id}`,
    dispatch,
  });
  if (response) return response.data;
};

export const updatePendingAccountsCategory = async (
  dispatch: Dispatch,
  id: number,
  data: any,
) => {
  const response = await requestHandler({
    method: 'patch',
    url: `internal/pendingAccountCategory/${id}`,
    data,
    dispatch,
  });
  if (response) return response.data;
};

export const getDocumentAvailablesForPaymentRequest = async (
  dispatch: Dispatch,
) => {
  const response = await requestHandler({
    method: 'get',
    url: `internal/paymentRequest/documents`,
    dispatch,
  });
  if (response) return response.data;
};

export const getPendingAccountsAvailablesForPaymentRequest = async (
  dispatch: Dispatch,
) => {
  const response = await requestHandler({
    method: 'get',
    url: `internal/paymentRequestPassive/pendingAccounts`,
    dispatch,
  });
  if (response) return response.data;
};

export const getBankAccountsForNominee = async (
  dispatch: Dispatch,
  businessIdentifier: string,
) => {
  const response = await requestHandler({
    method: 'get',
    url: `internal/company/getBankAccountsForNominee/${businessIdentifier}`,
    dispatch,
  });
  if (response) return response.data;
};

export const getLastTGRScrapDates = async (
  dispatch: Dispatch,
  businessId: string,
) => {
  const response = await requestHandler({
    method: 'get',
    url: `internal/enrollment/getAllLastScrapsByBusinessAndScraperIds?businessId=${businessId}&scraperIds=tgr-collection,tgr-debt,tgr-agreement`,
    dispatch,
  });
  if (response) return response.data;
};

export const createNotRecognizedPayment = async (
  dispatch: Dispatch,
  data: any,
) => {
  const response = await requestHandler({
    method: 'post',
    url: `internal/conciliation/notRecognized`,
    data,
    dispatch,
  });
  if (response) return response.data;
};

export const getAllNotRecognizedPayments = async (
  dispatch: Dispatch,
  query: string,
) => {
  const response = await requestHandler({
    method: 'get',
    url: `internal/notRecognizedPayment?${query}`,
    dispatch,
  });
  if (response) return response.data;
};

export const getNotRecognizedPayment = async (
  dispatch: Dispatch,
  id: number,
) => {
  const response = await requestHandler({
    method: 'get',
    url: `internal/notRecognizedPayment/${id}`,
    dispatch,
  });
  if (response) return response.data;
};

export const createAndAssignPendingAccountsToNotRecognizedPayment = async (
  dispatch: Dispatch,
  id: number,
  data: any,
) => {
  const response = await requestHandler({
    method: 'post',
    url: `internal/notRecognizedPayment/${id}/assignPendingAccounts`,
    data,
    dispatch,
  });
  if (response) return response.data;
};

export const getAutoCollectionConfig = async (
  dispatch: Dispatch,
  businessIdentifier: string,
) => {
  const response = await requestHandler({
    method: 'get',
    url: `internal/collection/options/autoCollection/${businessIdentifier}`,
    dispatch,
  });
  if (response) return response.data;
};
export const getDocumentAlerts = async (dispatch: Dispatch) => {
  const response = await requestHandler({
    method: 'get',
    url: `internal/documentAlert`,
    dispatch,
  });
  if (response) return response.data;
};

export const postAutoCollectionConfig = async (
  dispatch: Dispatch,
  businessIdentifier: string,
  contactIds: number[],
  daysToCollect: number,
  active: boolean,
) => {
  const response = await requestHandler({
    method: 'post',
    url: `internal/collection/options/autoCollection`,
    data: { businessIdentifier, contactIds, daysToCollect, active },
    dispatch,
  });
  if (response) return response.data;
};

export const getDocumentAlertsByCompany = async (dispatch: Dispatch) => {
  const response = await requestHandler({
    method: 'get',
    url: `internal/documentAlert/company`,

    dispatch,
  });
  if (response) return response.data;
};

export const getFutureCollectionManagementsByCompanyId = async (
  dispatch: Dispatch,
  query: string,
) => {
  const response = await requestHandler({
    method: 'get',
    url: `internal/collection/next?${query}`,
    dispatch,
  });
  if (response) return response.data;
};

export const getBusinessRelationsByStatus = async (
  dispatch: Dispatch,
  status: string,
) => {
  const response = await requestHandler({
    method: 'get',
    url: `internal/users/businessRelations/status/${status}`,
    dispatch,
  });
  if (response) return response.data;
};

export const getBusinessRelationsByMultipleStatus = async (
  dispatch: Dispatch,
  statuses: string[],
) => {
  const statusQuery = statuses.join(',');
  const response = await requestHandler({
    method: 'get',
    url: `internal/users/businessRelations/multiple-status?status=${statusQuery}`,
    dispatch,
  });
  if (response) return response.data;
};

export const getFactoringRequestCreditLine = async (
  dispatch: Dispatch,
  requestId: number,
) => {
  const response = await requestHandler({
    method: 'get',
    url: `internal/factoring/request/${requestId}/creditLine`,
    dispatch,
  });
  if (response) return response.data;
};

export const getFactoringCreditLineInfo = async (
  dispatch: Dispatch,
  requestId: number,
  queryParams: string,
) => {
  const response = await requestHandler({
    method: 'get',
    url: `internal/factoring/request/${requestId}/creditLineInfo?${queryParams}`,
    dispatch,
  });
  if (response) return response.data;
};
